/**
 * A small stale-while-revalidate cache for paged list responses.
 *
 * Returning to a list — after opening a profile and pressing Back — should paint the
 * rows you were looking at immediately, then quietly refresh them, rather than showing
 * a skeleton while the same request runs again.
 *
 * Backed by sessionStorage so it also survives a refresh, with an in-memory layer so
 * repeat reads don't re-parse JSON. Scoped to the browser tab's session.
 */

/**
 * Cached pages to keep. A page of profiles is heavy, and paging through a large
 * directory would otherwise grow the cache for the whole session. Map preserves
 * insertion order, so the oldest entry is the first key.
 */
const MAX_ENTRIES = 12;

const memory = new Map<string, unknown>();

export function readListCache<T>(key: string): T | undefined {
  if (memory.has(key)) return memory.get(key) as T;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return undefined;
    const value = JSON.parse(raw) as T;
    memory.set(key, value);
    return value;
  } catch {
    // Unreadable or malformed entry — treat it as a miss.
    return undefined;
  }
}

/**
 * Drop every cached page for a list, by key prefix. Call after the user changes the
 * underlying data — importing or adding profiles — so the next read can't serve rows
 * from before their change.
 */
export function clearListCache(prefix: string): void {
  for (const key of [...memory.keys()]) {
    if (key.startsWith(prefix)) memory.delete(key);
  }
  try {
    const stale = Object.keys(sessionStorage).filter(key => key.startsWith(prefix));
    for (const key of stale) sessionStorage.removeItem(key);
  } catch {
    // Storage blocked — the in-memory copy is already cleared.
  }
}

export function writeListCache(key: string, value: unknown): void {
  memory.delete(key); // re-insert so a refreshed entry counts as the newest
  memory.set(key, value);

  while (memory.size > MAX_ENTRIES) {
    const oldest = memory.keys().next().value;
    if (oldest === undefined) break;
    memory.delete(oldest);
    try {
      sessionStorage.removeItem(oldest);
    } catch {
      // Storage blocked — nothing to evict there.
    }
  }

  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Over quota or storage blocked — the in-memory copy still serves this session.
  }
}
